<template>
  <div class="content-home-block">
    <div class="dashboard">
      <div class="dashboard-row">
        <!-- Локации -->
        <div class="dashboard-card small dx-card">
          <router-link class="dashboard-card-header" to="/locations">
            <span class="header-title">{{ $t('home.locations.headerTitle') }}</span>
            <img class="arrow-icon" src="@/assets/arrow.svg" alt="" />
          </router-link>

          <!-- Skeleton во время загрузки -->
          <template v-if="loading.locations">
            <div class="locations-skeleton">
              <div class="skeleton-item" v-for="n in 8" :key="n">
                <div class="skeleton-row short"></div>
              </div>
            </div>
          </template>

          <template v-else>
            <DxScrollView
              v-if="statistics.locations && statistics.locations.length > 0"
              :scroll-by-content="true"
              :scroll-by-thumb="true"
              show-scrollbar="onHover"
              height="100%"
            >
              <router-link v-for="(location, index) in statistics.locations" :key="index" class="location-item" :to="`/location/${location.id}`" >
                <div>{{ location.name }}</div>
              </router-link>
            </DxScrollView>
          </template>
        </div>

        <!-- Устройства -->
        <div class="dashboard-card small dx-card">
          <router-link class="dashboard-card-header" to="/devices">
            <span class="header-title">{{ $t('home.devices.headerTitle') }}</span>
            <img class="arrow-icon" src="@/assets/arrow.svg" alt="" />
          </router-link>

          <template v-if="loading.devicesStats">
            <div class="stats-skeleton">
              <div class="skeleton-item" v-for="n in 4" :key="n">
                <div class="skeleton-row short"></div>
                <div class="skeleton-row long"></div>
              </div>
            </div>
          </template>

          <template v-else>
            <div>
              <div class="stat">
                <div class="stat-label-container">
                  <span class="stat-label">{{ $t('home.devices.total') }}</span>
                  <span class="stat-percent">{{ statistics.devicesStats.total.percent }}%</span>
                </div>

                <div class="stat-value-container">
                  <router-link class="stat-value link" :to="{ name: 'devices' }">{{ formatNumber(statistics.devicesStats.total.count) }}</router-link>
                </div>
              </div>

              <div class="stat red">
                <div class="stat-label-container">
                  <span class="stat-label">{{ $t('home.devices.notAvailable') }}</span>
                  <span class="stat-percent">{{ formatPercent(statistics.devicesStats.notAvailable.percent) }}</span>
                </div>

                <div class="stat-value-container">
                  <span class="stat-value link" @click="goToDevicesNotAvailable">{{ formatNumber(statistics.devicesStats.notAvailable.count) }}</span>
                  <span class="icon">!</span>
                </div>
              </div>

              <div class="stat">
                <div class="stat-label-container">
                  <span class="stat-label">{{ $t('home.devices.managed') }}</span>
                  <span class="stat-percent">{{ formatPercent(statistics.devicesStats.managed.percent) }}</span>
                </div>

                <div class="stat-value-container">
                  <span class="stat-value link" @click="goToDevicesManaged">{{ formatNumber(statistics.devicesStats.managed.count) }}</span>
                </div>
              </div>

              <div class="stat">
                <div class="stat-label-container">
                  <span class="stat-label">{{ $t('home.devices.new7') }}</span>
                  <span class="stat-percent">{{ formatPercent(statistics.devicesStats.new7.percent) }}</span>
                </div>

                <div class="stat-value-container">
                  <span class="stat-value link" @click="goToDevicesNew7">{{ formatNumber(statistics.devicesStats.new7.count) }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Агенты -->
        <div class="dashboard-card small">
          <router-link class="dashboard-card-header" to="/agents">
            <span class="header-title">{{ $t('home.agents.headerTitle') }}</span>
            <img class="arrow-icon" src="@/assets/arrow.svg" alt="" />
          </router-link>

          <template v-if="loading.agentsStats">
            <div class="stats-skeleton">
              <div class="skeleton-item" v-for="n in 4" :key="n">
                <div class="skeleton-row short"></div>
                <div class="skeleton-row long"></div>
              </div>
            </div>
          </template>

          <template v-else>
            <div>
              <div class="stat">
                <div class="stat-label-container">
                  <span class="stat-label">{{ $t('home.agents.total') }}</span>
                  <span class="stat-percent">{{ statistics.agentsStats.total.percent }}%</span>
                </div>

                <div class="stat-value-container">
                  <router-link class="stat-value link" :to="{ name: 'agents' }">{{ formatNumber(statistics.agentsStats.total.count) }}</router-link>
                </div>
              </div>

              <div class="stat red">
                <div class="stat-label-container">
                  <span class="stat-label">{{ $t('home.agents.notAvailable') }}</span>
                  <span class="stat-percent">{{ formatPercent(statistics.agentsStats.notAvailable.percent) }}</span>
                </div>

                <div class="stat-value-container">
                  <span class="stat-value link" @click="goToAgentsNotAvailable">{{ formatNumber(statistics.agentsStats.notAvailable.count) }}</span>
                  <span class="icon">!</span>
                </div>
              </div>

              <div class="stat">
                <div class="stat-label-container">
                  <span class="stat-label">{{ $t('home.agents.online1Hour') }}</span>
                  <span class="stat-percent">{{ formatPercent(statistics.agentsStats.online1Hour.percent) }}</span>
                </div>

                <div class="stat-value-container">
                  <span class="stat-value link" @click="goToAgentsOnline1Hour">{{ formatNumber(statistics.agentsStats.online1Hour.count) }}</span>
                </div>
              </div>

              <div class="stat">
                <div class="stat-label-container">
                  <span class="stat-label">{{ $t('home.agents.outdated') }}</span>
                  <span class="stat-percent">{{ formatPercent(statistics.agentsStats.outdated.percent) }}</span>
                </div>

                <div class="stat-value-container">
                  <span class="stat-value link" @click="goToAgentsOutdated">{{ formatNumber(statistics.agentsStats.outdated.count) }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Объем печати -->
        <div class="dashboard-card small">
          <div class="dashboard-card-header">
            <span class="header-title">{{ $t('home.printVolume.headerTitle') }}</span>
          </div>

          <template v-if="loading.printVolume">
            <div class="stats-skeleton">
              <div class="skeleton-item" v-for="n in 4" :key="n">
                <div class="skeleton-row short"></div>
                <div class="skeleton-row long"></div>
              </div>
            </div>
          </template>

          <template v-else>
            <div>
              <div class="stat">
                <div class="stat-label-container">
                  <span class="stat-label">{{ $t('home.printVolume.total') }}</span>
                </div>

                <div class="stat-value-container">
                  <span class="stat-value">{{ formatNumber(statistics.printVolume.total) }}</span>
                </div>
              </div>

              <div class="stat">
                <div class="stat-label-container">
                  <span class="stat-label">{{ $t('home.printVolume.bw') }}</span>
                </div>

                <div class="stat-value-container">
                  <span class="stat-value aqua">{{ formatNumber(statistics.printVolume.bw) }}</span>
                </div>
              </div>

              <div class="stat">
                <div class="stat-label-container">
                  <span class="stat-label">{{ $t('home.printVolume.color') }}</span>
                </div>

                <div class="stat-value-container">
                  <span class="stat-value yellow-green">{{ formatNumber(statistics.printVolume.color) }}</span>
                </div>
              </div>

              <div class="stat">
                <div class="stat-label-container">
                  <span class="stat-label">{{ $t('home.printVolume.a3') }}</span>
                </div>

                <div class="stat-value-container">
                  <span class="stat-value">{{ formatNumber(statistics.printVolume.a3) }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="dashboard-row">
        <!-- Топ 10 печатающих -->
        <div class="dashboard-card table">
          <div class="dashboard-card-header">
            <span class="header-title">{{ $t('home.mostPrinting.headerTitle') }}</span>
          </div>

          <template v-if="loading.mostPrintingDevices">
            <div class="locations-skeleton">
              <div class="skeleton-item" v-for="n in 10" :key="n">
                <div class="skeleton-row short"></div>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="grid-wrapper">
              <dx-data-grid
                :data-source="statistics.mostPrintingDevices"
                :column-auto-width="true"
                :show-borders="false"
                :show-column-headers="false"
                :show-column-lines="false"
                :show-row-lines="false"
                height="100%"
                class="popular-printers-grid"
              >
                <dx-scrolling mode="virtual"/>

                <dx-column cell-template="progressTemplate" />

                <dx-column
                  data-field="printVolume"
                  data-type="number"
                  :format="{ type: 'fixedPoint', precision: 0 }"
                  :width="90"
                />

                <template #progressTemplate="{ data }">
                  <router-link
                    :to="{ name: 'device', params: { id: data.data.id }}"
                    class="progress-container"
                    @mouseenter.native="hoveredId = data.data.id"
                    @mouseleave.native="hoveredId = null"
                  >
                    <div class="progress-wrapper">
                      <dx-progress-bar
                        :min="0"
                        :max="maxVolume"
                        :value="data.data.printVolume"
                        :show-status="false"
                        class="custom-progress"
                        :class="{ dimmed: hoveredId && hoveredId !== data.data.id }"
                      />
                      <div class="progress-text">{{ data.data.modelCode }}</div>
                    </div>
                  </router-link>
                </template>
              </dx-data-grid>
            </div>
          </template>
        </div>

        <!-- График объема печати -->
        <div class="dashboard-card full-width">
          <div class="dashboard-card-header">
            <span class="header-title">{{ $t('home.dayColorCounts.headerTitle') }}</span>
          </div>

          <dx-chart
            :data-source="statistics.dayColorCounts"
            class="chart-print-volume"
            height="100%"
          >
            <dx-common-series-settings
              argument-field="day"
              type="spline"
            />

            <dx-legend :visible="false" />

            <dx-crosshair :enabled="true" color="#20304a80">
              <dx-label :visible="false" />
              <dx-horizontal-line :visible="false" />
            </dx-crosshair>

            <dx-tooltip
              :enabled="true"
              :shared="true"
              location="center"
              :behavior="{ showTooltipOnHover: true }"
              content-template="tooltip1"
            />
            <template #tooltip1="{ data }">
              <div class="my-legend">
                <div class="my-legend-title">{{ data.point.data.day }}</div>

                <div class="tooltip-row">
                  <div class="tooltip-color dark-blue-background"></div>
                  <div class="tooltip-label">{{ $t('home.printVolume.total') }}</div>
                  <div class="tooltip-cell-value">{{ formatNumber(data.point.data.totalCnt) }}</div>
                </div>

                <div class="tooltip-row">
                  <div class="tooltip-color aqua-background"></div>
                  <div class="tooltip-label">{{ $t('home.printVolume.bw') }}</div>
                  <div class="tooltip-cell-value">{{ formatNumber(data.point.data.bwCnt) }}</div>
                </div>

                <div class="tooltip-row">
                  <div class="tooltip-color yellow-green-background"></div>
                  <div class="tooltip-label">{{ $t('home.printVolume.color') }}</div>
                  <div class="tooltip-cell-value">{{ formatNumber(data.point.data.colorCnt) }}</div>
                </div>
              </div>
            </template>

            <dx-series
              value-field="totalCnt"
              argument-field="day"
              color="#000"
            >
              <dx-point
                :visible="true"
                :size="0"
                color="transparent"
                hover-mode="allArgumentPoints"
                :hover-style="{
                  size: 8,
                  color: '#000'
                }"
              />
            </dx-series>

            <dx-series
              value-field="bwCnt"
              argument-field="day"
              color="#2196f3"
            >
              <dx-point
                :visible="true"
                :size="0"
                color="transparent"
                hover-mode="allArgumentPoints"
                :hover-style="{
                  size: 8,
                  color: '#2196f3'
                }"
              />
            </dx-series>

            <dx-series
              value-field="colorCnt"
              argument-field="day"
              color="#f5a623"
            >
              <dx-point
                :visible="true"
                :size="0"
                color="transparent"
                hover-mode="allArgumentPoints"
                :hover-style="{
                  size: 8,
                  color: '#f5a623'
                }"
              />
            </dx-series>
          </dx-chart>

          <dx-load-panel v-if="printerStatusCodes" :visible="loading.dayColorCounts" :position="{ of: '.chart-print-volume' }" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import resize from 'vue-resize-directive';
import { mapCacheActions } from "vuex-cache";

import appInfo from '../../app-info';
import baseView from '../base';

import { DxDataGrid, DxScrolling, DxColumn } from 'devextreme-vue/data-grid';
import {
  DxChart,
  DxCommonSeriesSettings,
  DxPoint,
  DxSeries,
  DxCrosshair,
  DxLabel,
  DxHorizontalLine,
  DxTooltip,
  DxLegend
} from 'devextreme-vue/chart';
import DxProgressBar from 'devextreme-vue/progress-bar';
import { DxScrollView } from 'devextreme-vue/scroll-view';
import { DxLoadPanel } from "devextreme-vue/load-panel";

import {
  getAgentsActualVersions,
  getStatisticsAgentsStats,
  getStatisticsDayColorCounts,
  getStatisticsDevicesStats,
  getStatisticsLocations,
  getStatisticsMostPrintingDevices,
  getStatisticsPrintVolume
} from '@/services/api';

import { formatNumber } from '@/utils/format';
import moment from "moment";

export default {
  extends: baseView,
  components: {
    DxDataGrid,
    DxScrolling,
    DxColumn,
    DxChart,
    DxCommonSeriesSettings,
    DxPoint,
    DxSeries,
    DxCrosshair,
    DxLabel,
    DxHorizontalLine,
    DxTooltip,
    DxLegend,
    DxProgressBar,
    DxScrollView,
    DxLoadPanel
  },
  data() {
    const sevenDaysLaterUTC = moment().subtract(7, 'days').toDate();
    const threeDaysLaterUTC = moment().subtract(3, 'days').toDate();
    const oneHourLaterUTC = moment().subtract(1, 'hours').toDate();

    return {
      title: appInfo.title,
      loading: {
        locations: false,
        devicesStats: false,
        agentsStats: false,
        printVolume: false,
        mostPrintingDevices: false,
        dayColorCounts: false
      },
      isDestroyed: false,
      hoveredId: null,
      printerStatusCodes: null,
      actualAgentVer: [],
      devicesManagedFilter: {
        filter: ['device.isActive', '=', true]
      },
      devicesNew7Filter: {
        filter: ['device.createDate', '>', sevenDaysLaterUTC]
      },
      agentsNotAvailableFilter: {
        filter: ['lastActivity', '<', threeDaysLaterUTC]
      },
      agentsOnline1HourFilter: {
        filter: ['lastActivity', '>', oneHourLaterUTC]
      },
      statistics: {
        locations: [],
        devicesStats: {
          total: { count: 0, percent: 0 },
          notAvailable: { count: 0, percent: 0 },
          managed: { count: 0, percent: 0 },
          new7: { count: 0, percent: 0 }
        },
        agentsStats: {
          total: { count: 0, percent: 0 },
          notAvailable: { count: 0, percent: 0 },
          online1Hour: { count: 0, percent: 0 },
          outdated: { count: 0, percent: 0 }
        },
        printVolume: {
          total: 0,
          bw: 0,
          color: 0,
          a3: 0
        },
        mostPrintingDevices: [],
        dayColorCounts: []
      },
    }
  },
  computed: {
    maxVolume() {
      return Math.max(...this.statistics.mostPrintingDevices.map(p => p.printVolume));
    },
    devicesNotAvailableFilter() {
      return this.printerStatusCodes ? { filter: ['device.status.id', '=', this.printerStatusCodes['NOT_AVAILABLE'].id] } : null;
    },
    agentsOutdatedFilter() {
      if (this.actualAgentVer.length === 0)
        return null;

      return {
        filter: this.buildAgentFilterVer(this.actualAgentVer)
      }
    },
  },
  directives: {
    resize,
  },
  methods: {
    ...mapCacheActions(["getPrinterStatusCodes"]),
    formatNumber,
    goToDevicesNotAvailable() {
      this.$router.push({ name: 'devices', params: this.devicesNotAvailableFilter});
    },
    goToDevicesManaged() {
      this.$router.push({ name: 'devices', params: this.devicesManagedFilter});
    },
    goToDevicesNew7() {
      this.$router.push({ name: 'devices', params: this.devicesNew7Filter});
    },
    goToAgentsNotAvailable() {
      this.$router.push({ name: 'agents', params: this.agentsNotAvailableFilter});
    },
    goToAgentsOnline1Hour() {
      this.$router.push({ name: 'agents', params: this.agentsOnline1HourFilter});
    },
    goToAgentsOutdated() {
      this.$router.push({ name: 'agents', params: this.agentsOutdatedFilter});
    },
    buildAgentFilterVer(arr) {
      if (arr.length === 1) {
        return ['agentVersion', 'notcontains', arr[0]];
      }

      return arr.map(v => ['agentVersion', 'notcontains', v])
        .reduce((acc, item) => acc.length ? [...acc, 'and', item] : [item], []);
    },
    formatPercent(value) {
      return value.toFixed(1) + '%';
    },
    async loadStatisticPart(key, request) {
      this.loading[key] = true;

      try {
        const value = await request();

        if (!this.isDestroyed && value !== null && value !== undefined) {
          this.statistics[key] = value;
        }
      } catch (err) {
        if (!this.isDestroyed) {
          this.onDataError(err);
        }
      } finally {
        if (!this.isDestroyed) {
          this.loading[key] = false;
        }
      }
    },
    async loadPrinterStatusCodes() {
      try {
        const printerStatusCodes = await this.getPrinterStatusCodes();
        const statusCode = [];
        printerStatusCodes.forEach(item => {
          statusCode[item.code] = item;
        });
        if (!this.isDestroyed) {
          this.printerStatusCodes = statusCode;
        }
      } catch (err) {
        if (!this.isDestroyed) {
          this.onDataError(err);
        }
      }
    },
    async loadAgentsActualVersions() {
      try {
        const actualAgentVer = await getAgentsActualVersions();
        if (!this.isDestroyed && actualAgentVer) {
          this.actualAgentVer = actualAgentVer;
        }
      } catch (err) {
        if (!this.isDestroyed) {
          this.onDataError(err);
        }
      }
    },
    load() {
      this.loadPrinterStatusCodes();
      this.loadAgentsActualVersions();
      this.loadStatisticPart('locations', getStatisticsLocations);
      this.loadStatisticPart('devicesStats', getStatisticsDevicesStats);
      this.loadStatisticPart('agentsStats', getStatisticsAgentsStats);
      this.loadStatisticPart('printVolume', getStatisticsPrintVolume);
      this.loadStatisticPart('mostPrintingDevices', getStatisticsMostPrintingDevices);
      this.loadStatisticPart('dayColorCounts', getStatisticsDayColorCounts);
    },
  },
  async created() {
    document.title = appInfo.title;
    this.$store.commit('setBreadcrumb', []);
    this.load();
  },
  beforeDestroy() {
    this.isDestroyed = true;
  },
  watch: {}
}
</script>

<style lang="scss">
.content-home-block {
  height: 100%;
  padding-left: 40px;
  padding-right: 40px;
  padding-top: 20px;
}

.dashboard {
  height: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 40px;

  .dashboard-row {
    display: flex;
    flex-direction: row;
    column-gap: 20px;
    min-height: 0;

    &:first-child {
      // height: 45%;
      flex: 0 0 50%;
    }

    &:last-child {
      flex: 1 1 auto;
      align-items: stretch;
    }

    .small {
      flex: 1 1 25%;
      min-width: 180px;

      display: flex;
      flex-direction: column;
      // height: 500px;
    }

    .table {
      max-width: 500px;
      display: flex;
      flex-direction: column;
      flex: 0 0 30%;
      min-height: 0;
    }

    .full-width {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      flex-direction: column;

      .chart-print-volume {
        flex: 1 1 auto;
        min-height: 0;
      }
    }

    .dashboard-card {
      background-color: #fff;
      padding: 0 24px 20px;
      overflow: hidden;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

      .dashboard-card-header {
        margin: 0 -24px;
        padding: 20px 24px;
        border-bottom: 1px solid #ebebeb;
        background-color: #E7F7FF;
        display: flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: space-between;
        text-decoration: none;

        .header-title {
          font-size: 18px;
          font-weight: 600;
        }

        .arrow-icon {
          transition: transform 0.3s ease;
        }

        &:has(.arrow-icon):hover {
          background-color: #D6F1FF;
        }

        &:has(.arrow-icon):hover .arrow-icon {
          transform: rotate(90deg);
        }
      }

      .grid-wrapper {
        flex: 1 1 auto;
        min-height: 0;
      }

      /* Skeleton */
      .locations-skeleton {
        flex: 1 1 auto;
        overflow: hidden;

        .skeleton-row.short {
          height: 15px;
          margin-bottom: 0px;
        }
      }

      .stats-skeleton {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 12px 0;

        flex: 1 1 auto;
        overflow: hidden;
      }

      .skeleton-item {
        border-bottom: 1px solid #ebebeb;
        padding: 10px 0;

        &:last-child {
          border-bottom: none;
        }
      }

      .skeleton-row {
        background: linear-gradient(90deg, #f0f0f0 25%, #e4e4e4 37%, #f0f0f0 63%);
        background-size: 400% 100%;
        animation: shimmer 1.4s ease infinite;
        border-radius: 4px;
      }

      .skeleton-row.short {
        width: 100%;
        height: 12px;
        margin-bottom: 8px;
      }

      .skeleton-row.long {
        width: 30%;
        height: 20px;
      }

      @keyframes shimmer {
        0% { background-position: -400px 0; }
        100% { background-position: 400px 0; }
      }

      /* Стили для каждого элемента внутри DxScrollView */
      .location-item {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        // padding: 18px 0 12px 0;
        padding: 7px 0;
        border-bottom: 1px solid #ebebeb;
        transition: background-color 0.2s ease;
        text-decoration: none;

        .location-link {
          text-decoration: none;
        }

        &:hover {
          background-color: #f5f5f5;
          cursor: pointer;
        }

        &:last-child {
          border-bottom: none;
        }
      }

      /* Статистика */
      .stat {
        display: flex;
        flex-direction: column;
        margin-top: 12px;
        padding: 12px 5px;
        border-bottom: 1px solid #ebebeb;
        color: #20304A;

        &:first-child {
          padding-top: 0;
        }
        &:last-child {
          border-bottom: none;
        }

        .stat-label-container {
          display: flex;
          justify-content: space-between;
          align-items: center;

          .stat-label {
            font-size: 14px;
            font-weight: 500;
            color: rgba(32, 48, 74, 0.8);
          }

          .stat-percent {
            font-size: 13px;
            color: rgba(32, 48, 74, 0.8);
          }
        }

        .stat-value-container {
          display: flex;
          justify-content: space-between;
          align-items: center;

          .stat-value {
            // font-size: clamp(16px, 1.8vw, 42px);
            font-size: clamp(16px, 2.2vw, 50px);
            font-weight: 600;
            line-height: 1.1;
            color: #20304a;
          }

          .aqua {
            color: #00A4F9;
          }
          .yellow-green {
            color: #FCB65E;
          }
          .dark-blue {
            color: #20304A;
          }

          .icon {
            width: 18px;
            height: 18px;
            background: #b71c1c;
            color: #fff;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: bold;
          }
        }
      }

      .stat.red {
        color: #d93025;
        border: 1px solid #b71c1c;
        border-radius: 6px;
        background-color: rgba(217, 48, 37, 0.1);
      }

      .stat.red .stat-label,
      .stat.red .stat-percent,
      .stat.red .stat-value {
        color: inherit;
      }

      /* Статистика самых печатающих */
      .popular-printers-grid {
        margin-top: 5px;

        .progress-container {
          display: flex;
          align-items: center;
          width: 100%;

          .progress-wrapper {
            position: relative;
            flex: 1;

            .custom-progress {
              width: 100%;
              height: 24px;

              .dx-progressbar-container {
                height: 24px;
              }

              .dx-progressbar-container {
                background-color: transparent !important;
                border: none !important;
              }

              .dx-progressbar-range {
                position: relative;
                background-color: transparent !important;
                border-color: transparent !important;
              }

              .dx-progressbar-range::after {
                content: "";
                position: absolute;
                top: 0;
                left: 0;
                height: 100%;
                width: 100%; /* соответствует фактической ширине range */
                background-color: rgba(0, 198, 255, 0.3);
                border: 1px solid rgba(0, 198, 255, 0.3);
                pointer-events: none;
                transition: background-color 0.25s ease, border-color 0.25s ease;
              }
            }

            .custom-progress.dimmed .dx-progressbar-range::after {
              background-color: rgba(0, 198, 255, 0.1);
              border-color: rgba(0, 198, 255, 0.1);
            }

            .progress-text {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: flex-start;
              color: rgba(32, 48, 74, 0.8);
              font-size: 14px;
              pointer-events: none;
            } 
          }
        }
      }
    }
  }
}


.my-legend-title {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 500;

  margin-left: -18px;   /* компенсируем отступ от tooltip */
  margin-right: -18px;
  padding-left: 18px;   /* возвращаем текст на место */
  padding-right: 18px;

  padding-bottom: 14px;
  border-bottom: 1px solid #ebebeb;
}

.tooltip-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 3px 0;
  gap: 12px;
  font-size: 14px;

  .aqua-background {
    background-color: #00A4F9;
  }
  .yellow-green-background {
    background-color: #FCB65E;
  }
  .dark-blue-background {
    background-color: #20304A;
  }
  .tooltip-color {
    width: 20px;
    height: 4px;
    border-radius: 4px;
  }

  .tooltip-label {
    flex: 1;
    color: #20304A;
  }

  .tooltip-cell-value {
    color: rgba(32, 48, 74, 0.8);
  }
}

@media screen and (max-width: 2199px) {
  .dashboard {
    row-gap: 16px !important;

    .dashboard-row {
      column-gap: 16px !important;

      .dashboard-card {
        padding: 0 20px 18px !important;

        .dashboard-card-header {
          margin: 0 -24px;
          padding: 16px 24px !important;

          .header-title {
            font-size: 16px !important;
          }
        }

        .stat {
          margin-top: 8px;
          padding: 6px 5px;
        }
      }
    }
  }
}

@media screen and (min-width: 1579px) and (max-width: 1779px) {
  .dashboard {
    row-gap: 6px !important;

    .dashboard-row {
      column-gap: 12px !important;

      &:first-child {
        flex: 0 0 55%;
      }

      .dashboard-card {
        padding: 0 20px 18px !important;

        .dashboard-card-header {
          margin: 0 -24px;
          padding: 12px 24px !important;

          .header-title {
            font-size: 16px !important;
          }
        }

        .stat {
          margin-top: 6px;
          padding: 6px 5px;

          .stat-value {
            font-size: clamp(16px, 3vw, 50px) !important;
          }
        }

        .stats-skeleton {
          gap: 8px;
          padding: 8px 0;

          .skeleton-row.short {
            margin-bottom: 6px;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 1579px) {
  .dashboard {
    row-gap: 6px !important;

    .dashboard-row {
      column-gap: 12px !important;

      &:first-child {
        flex: 0 0 55%;
      }

      .dashboard-card {
        padding: 0 20px 18px !important;

        .dashboard-card-header {
          margin: 0 -24px;
          padding: 12px 24px !important;

          .header-title {
            font-size: 16px !important;
          }
        }

        .stat {
          margin-top: 6px;
          padding: 6px 5px;

          .stat-value {
            font-size: clamp(16px, 2.2vw, 50px) !important;
          }
        }

        .stats-skeleton {
          gap: 8px;
          padding: 8px 0;

          .skeleton-row.short {
            margin-bottom: 6px;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 1279px) {
  .content-home-block {
    padding-top: 12px;
    padding-left: 20px;
    padding-right: 20px;
  }

  .dashboard {
    row-gap: 8px !important;

    .dashboard-row {
      column-gap: 8px !important;

      .dashboard-card {
        padding: 0 16px 18px !important;

        .dashboard-card-header {
          margin: 0 -16px;
          padding: 8px 16px !important;

          .header-title {
            font-size: 16px !important;
          }
        }

        .stat {
          margin-top: 4px;
          padding: 2px 5px;

          .stat-value {
            // font-size: clamp(16px, 3vw, 50px) !important;
          }
        }

        .stats-skeleton {
          gap: 8px;
          padding: 8px 0;

          .skeleton-item {
            padding: 8px 0;
          }

          .skeleton-row.short {
            margin-bottom: 6px;
          }

          .skeleton-row.long {
            height: 18px;
          }
        }
      }
    }
  }
}
</style>
